import React, {useCallback} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {OfferInteraction} from 'components/Offer/Shared/OfferInteraction/OfferInteraction'
import {getAnchorPriceTotal} from 'modules/sapiSearch/selectors'

import {ButtonSize} from '@vio/atlas/Button'
import isBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

import {isRoomOffer, isSearchOffer, PriceTrendOffer} from '../../types'
import {PriceTrendMessageKey} from '../../utils/messages'
import {PriceTrendButton} from '../PriceTrendButton'
import {PriceTrendRedirectLayout} from '../PriceTrendRedirectLayout'

// Wrapper to prevent OfferInteraction from taking full width (it has width: 100% by default)
const OfferInteractionWrapper = styled.div<{fitContent?: boolean}>`
  ${({fitContent}) => fitContent && 'display: inline-flex;'}
`

interface PriceTrendOfferButtonProps {
  /** The message key to display on the button */
  messageKey: PriceTrendMessageKey
  /** The price value to display */
  priceValue: number
  /** The currency code */
  currency: string | null
  /**
   * The selected offer.
   * - Room Offer (from Rooms API): Used for BOFH/Vio offers, always treated as BOFH
   * - SearchOffer (from Search API): Used for redirect providers
   */
  selectedOffer: PriceTrendOffer
  /** The hotel ID */
  hotelId: string
  /** Callback fired when the button is clicked for BOFH offers (scroll to rooms) */
  onButtonClick?: (messageKey: PriceTrendMessageKey | null) => void
  /** Callback fired for every CTA click (both BOFH and redirect offers), used for analytics */
  onCTAClicked?: () => void
  /** Variant to control layout: mobile shows logo+price for redirects, desktop shows simple button */
  isDesktop?: boolean
  /** Button size (for desktop variant) */
  size?: ButtonSize
}

const COMPONENT_NAME = 'PriceInsightsCTA'

/**
 * Wrapper component for PriceTrendButton that handles the distinction between
 * BOFH offers (scroll to rooms) and redirect offers (open external provider).
 * Uses OfferInteraction to handle all offer click interactions consistently.
 *
 * Variants:
 * - mobile: For redirect offers, displays provider logo alongside the price
 * - desktop: For redirect offers, displays simple button with arrow
 */
export const PriceTrendOfferButton = ({
  messageKey,
  priceValue,
  currency,
  selectedOffer,
  hotelId,
  onButtonClick,
  onCTAClicked,
  isDesktop,
  size
}: PriceTrendOfferButtonProps) => {
  const anchorPriceTotal =
    useSelector(state => getAnchorPriceTotal(state, hotelId)) ?? 0

  // Determine if this is a BOFH (BoVio/FHT) offer:
  // - Room Offers (from Rooms API): Always BOFH
  // - SearchOffer (from Search API): Check intermediaryProvider via isBofhOffer
  // Note: SplitBooking is not supported by PriceTrend and is filtered out
  const isBofhOfferType =
    isRoomOffer(selectedOffer) ||
    (isSearchOffer(selectedOffer) && isBofhOffer(selectedOffer))
  const isRedirectOffer = !isBofhOfferType

  // Fire analytics for every CTA click, regardless of offer type.
  // Only call onButtonClick for BOFH offers to scroll to rooms — redirect offers
  // are sent to an external provider by OfferInteraction, no scroll needed.
  const handleOfferClick = useCallback(() => {
    onCTAClicked?.()
    if (isBofhOfferType) {
      onButtonClick?.(messageKey)
    }
  }, [isBofhOfferType, onButtonClick, onCTAClicked, messageKey])

  // Determine the message key for redirect offers based on variant
  const effectiveMessageKey: PriceTrendMessageKey =
    isRedirectOffer && isDesktop ? 'viewDealFromValue' : messageKey

  const renderButton = () => {
    // BOFH offer - same for both variants
    if (isBofhOfferType) {
      return (
        <PriceTrendButton
          messageKey={effectiveMessageKey}
          priceValue={priceValue}
          currency={currency}
          size={size}
        />
      )
    }

    // Redirect offer - desktop variant: simple button with arrow
    if (isDesktop) {
      return (
        <PriceTrendButton
          messageKey={effectiveMessageKey}
          priceValue={priceValue}
          currency={currency}
          size={size}
          hasArrow
        />
      )
    }

    // Redirect offer - mobile variant: logo + price + button layout
    // Only SearchOffer can be a redirect offer, so this cast is safe
    return (
      <PriceTrendRedirectLayout
        offer={selectedOffer as SearchOffer}
        hotelId={hotelId}
        priceValue={priceValue}
        currency={currency}
      />
    )
  }

  // For Room Offers (from Rooms API), we don't use OfferInteraction
  // since it's just a scroll action, not an offer click tracked by analytics
  // Room Offers are always BOFH in Vio context
  // Note: SplitBooking is not supported by PriceTrend and is filtered out
  if (isRoomOffer(selectedOffer)) {
    return (
      <OfferInteractionWrapper>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div onClick={handleOfferClick}>{renderButton()}</div>
      </OfferInteractionWrapper>
    )
  }

  // For SearchOffer, use OfferInteraction for analytics tracking
  // This handles both BOFH SearchOffers (scroll to rooms) and redirect SearchOffers
  return (
    <OfferInteractionWrapper fitContent={isDesktop && isRedirectOffer}>
      <OfferInteraction
        anchorPriceTotal={anchorPriceTotal}
        component={COMPONENT_NAME}
        hotelId={hotelId}
        offer={selectedOffer as SearchOffer}
        offerPosition={0}
        onClick={handleOfferClick}
      >
        {renderButton()}
      </OfferInteraction>
    </OfferInteractionWrapper>
  )
}
